import { useEffect, useMemo } from "react";
import { useAppSelector } from "app/store";
import { useTeamManagement } from "./useTeamManagement";
import { Team } from "models/schemas";
import { useGetTeamByIdQuery } from "pages/team_settings/endpoints";
import { GaiaConstants } from "utils";

/**
 * Hook to load team settings for guest users when current resource team changes
 * @param currentResourceTeam The current resource team name
 */
export const useTeamSettingsLoader = (currentResourceTeam: string) => {
  const allTeams: any = useAppSelector((state) => state.teamSettings?.allTeams);
  const currentGuestTeam = useAppSelector(
    (state) => state.teamSettings.currentGuestTeam,
  );
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam,
  );

  const { setGuestTeamDataAndSchema } = useTeamManagement();

  // Determine if we need to fetch team settings for guest users
  const shouldFetchTeamSettings = useMemo(() => {
    if (
      !currentResourceTeam ||
      !allTeams?.teams ||
      currentResourceTeam === GaiaConstants.PLACE_HOLDER
    ) {
      return false;
    }

    // Check if user is actually a guest (not a team member to this specific team)
    const isGuest = !allTeams.teams.some(
      (team: Team) => team.teamId === currentResourceTeam,
    );

    // Only fetch if user is guest and we don't have current guest team data for this team
    return (
      isGuest &&
      (!currentGuestTeam?.selectedTeamMainMetaData ||
        currentGuestTeam.selectedTeamMainMetaData?.teamId !==
          currentResourceTeam)
    );
  }, [currentResourceTeam, allTeams, currentGuestTeam]);

  const {
    data: teamData,
    isLoading: isLoadingTeamSettings,
    error: teamSettingsError,
  } = useGetTeamByIdQuery(
    { teamId: currentResourceTeam! },
    {
      skip:
        !shouldFetchTeamSettings ||
        currentResourceTeam === GaiaConstants.PLACE_HOLDER,
    },
  );

  // Update guest team data when fetched - only once when needed
  useEffect(() => {
    if (
      teamData?.team &&
      shouldFetchTeamSettings &&
      headerSelectedTeam === "guest" &&
      (!currentGuestTeam?.selectedTeamMainMetaData ||
        currentGuestTeam.selectedTeamMainMetaData.teamId !==
          teamData.team.teamId)
    ) {
      setGuestTeamDataAndSchema(teamData.team);
    }
  }, [
    teamData,
    shouldFetchTeamSettings,
    headerSelectedTeam,
    currentGuestTeam,
    setGuestTeamDataAndSchema,
  ]);

  return {
    isLoadingTeamSettings,
    teamSettingsError,
    shouldFetchTeamSettings,
  };
};
